import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { CountryCode } from 'libphonenumber-js';

import { toE164, toNational } from '~/utils/phone-number-helpers';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import { usePublisherMe } from '~/hooks/api/usePublisherMe';
import { usePublisherGetProfile } from '~/hooks/api/usePublisherGetProfile';
import { usePublisherSetProfile } from '~/hooks/api/usePublisherSetProfile';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import UserProfileForm from '~/containers/UserProfile/UserProfileForm';
import messages from '~/containers/UserProfile/messages';

const routeApi = getRouteApi('/user-profile/account');

function UserProfileAccount() {
  const { publisherId } = routeApi.useSearch();
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const { data: masterdataSettings } = useSettingsByCategory('masterdata');

  const { data: publisherData, isLoading: isLoadingPublisher } = usePublisherMe(
    {
      enabled: !publisherId,
    }
  );

  const { data: profile } = usePublisherGetProfile(
    publisherId ? parseInt(publisherId, 10) : publisherData?.id || 0,
    {
      enabled: !isLoadingPublisher || Boolean(publisherId),
    }
  );

  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const setPublisherProfileMutation = usePublisherSetProfile(profile?.id!, {
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.editUserProfileSuccess),
      });
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.editUserProfileFailure),
      });
    },
  });

  if (!profile) {
    return null;
  }

  return (
    <UserProfileForm
      onSubmit={(profileValues) => {
        setPublisherProfileMutation.mutate({
          ...(profile || {}),
          ...profileValues,
          phone: toE164(
            profileValues.phone || undefined,
            masterdataSettings?.defaultRegionCallingCode as CountryCode
          ),
        });
      }}
      initialValues={{
        phone: toNational(
          profile.phone || undefined,
          masterdataSettings?.defaultRegionCallingCode as CountryCode
        ),
        languages: profile.languages,
      }}
      publisherName={profile.name}
      gender={profile.gender}
      congregation={profile.congregation}
      partnerName={profile.partnerName}
      privilege={profile.privilege}
      isPioneer={profile.isPioneer}
      mail={profile.email}
      isLoggedInUsersProfile={!publisherId}
    />
  );
}

export const Route = createFileRoute('/user-profile/account')({
  component: UserProfileAccount,
});
